import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Button, CircularProgress, Box } from '@mui/material';
import { useAuth } from "../contexts/AuthContext";

const SwitchToRole = () => {
  const { role } = useParams();
  const { user, switchRole, userRoles, activeRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => {
    // If user already has the active role, redirect
    if (activeRole === role) {
      navigate(`/${role}`);
    }
    
    // If user doesn't have the role, redirect to add role
    if (!userRoles.includes(role)) {
      navigate('/add-role');
    }
  }, [activeRole, role, userRoles, navigate]);
  
  const handleSwitch = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log(`Attempting to switch to role: ${role}`);
      const success = await switchRole(role);
      if (success) {
        console.log(`Successfully switched to role: ${role}, navigating to /${role}`);
        navigate(`/${role}`);
      } else {
        setError('Failed to switch role. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    // When canceling a role switch, go back to the original registration dashboard
    const originalUserType = user?.attributes && user.attributes['custom:user_type'];
    if (originalUserType) {
      console.log('Canceling role switch, returning to original dashboard:', originalUserType);
      navigate(`/${originalUserType}`);
    } else if (activeRole) {
      console.log('Canceling role switch, returning to active role dashboard:', activeRole);
      navigate(`/${activeRole}`);
    } else {
      navigate('/');
    }
  };
  
  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          Switch to {role.charAt(0).toUpperCase() + role.slice(1)} Role
        </Typography>
        
        <Typography variant="body1" paragraph>
          You're trying to access content that requires your {role} role. 
          Would you like to switch to your {role} profile?
        </Typography>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          
          <Button 
            variant="contained" 
            onClick={handleSwitch}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : `Switch to ${role.charAt(0).toUpperCase() + role.slice(1)}`}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SwitchToRole;
